import moment from 'moment';
import { isWithinDates } from '../../../common-client2/src/services/dateTime';
import { ongoingRequestStatuses, ongoingProposalStatuses } from '../../../EmploymentBillboard/constants';
import { getCurrentUkgUserDateTime, getUserFormats } from '../../../common-client2/src/services/ukgDateTime';

export const dateDisplayFormat = 'h:mm A';

/**
 * @param {Date|null} anchorDate
 * @param {Date} inputDate
 * @returns { number } The steps representation of the difference in hours from the anchor date.
 */
export const getAmountInMinutesFromRange = (anchorDate, inputDate) => {
    if (!inputDate || anchorDate.getTime() === inputDate.getTime()) {
        return 0;
    }

    const diff = Math.abs(inputDate - anchorDate);
    const diffInMinutes = (diff / (1000 * 3600)) * 60;
    return diffInMinutes;
};

/**
 * @param {Date} anchorDate
 * @param {number} diffInMinutes
 */
export const getAsDateFromMinutes = (anchorDate, diffInMinutes) => {
    const msInOneHour = 3600 * 1000;
    const diffInHours = diffInMinutes / 60; // 240/60 = 4
    const dateWithDiff = new Date(anchorDate.getTime() + (diffInHours * msInOneHour));
    return dateWithDiff;
};

/**
 * @param {Date} anchorDate
 * @param {number} diffInMinutes
 */
export const getDateFormatFromDiff = (anchorDate, diffInMinutes) => {
    const dateWithDiff = getAsDateFromMinutes(anchorDate, diffInMinutes);
    const formatToUse = getUserFormats().TimeFormat || dateDisplayFormat;
    return moment(dateWithDiff).format(formatToUse);
};

/**
 * @param {Date} date
 * @param {string} displayFormat
 */
export const formatSliderDate = (date, displayFormat = dateDisplayFormat) => moment(date).format(displayFormat);

/**
 * 
 * @typedef {{
 *  proposalId: number;
 * } & PartialOffer} ShiftSwapProposal
 * 
 * @typedef {{
 *  offerId: number;
 *  proposals: ShiftSwapProposal[];
 * } & PartialOffer} ShiftSwapOffer
 * 
 * @typedef {{
 *  alreadyOfferedInPartialStart: Date;
 *  alreadyOfferedInPartialEnd: Date;
 * }} MiscellaneousOffer
 * 
 * @typedef {{
 *  employeeShiftOffers: PartialOffer[];
 *  employeeMyShiftSwapOffers: ShiftSwapOffer[];
 *  myProposals: ShiftSwapOffer[];
 *  miscellaneousOffers: MiscellaneousOffer[];
 * }} StoreData
 * 
 * @typedef {typeof RequestStatus} IRequestStatus
 *
 * @typedef {{
 *  id: number;
 *  startDateTime: Date;
 *  endDateTime: Date;
 * }} ShiftRequestRange
 *
 * @typedef {{
 *  partialShift: { id: number };
 *  partialFromDate: Date;
 *  partialToDate: Date;
 *  status: IRequestStatus[keyof IRequestStatus];
 * }} PartialOffer
 *
 * @typedef {PartialOffer & {
 *  fromLabel: string;
 *  toLabel: string;
 * }} PartialOfferWithLabel
 *
 * @typedef {{ min: number; max: number }} SliderThresholds
 *
*/

/**
 * Returns ongoing partial requests of the day of the requested shift based on the id
 * @param {ShiftRequestRange} shiftRequestRange
 * @param {StoreData} storeData
 * @returns {PartialOfferWithLabel[]} Partially taken shift offers.
 */
export const getDisabledPartialPeriods = (shiftRequestRange, { employeeShiftOffers, employeeMyShiftSwapOffers, myProposals, miscellaneousOffers }) => {
    const endDate = new Date(shiftRequestRange.endDateTime);
    endDate.setHours(23, 59, 59);
    const fullShift = {
        start: shiftRequestRange.startDateTime,
        end: endDate, // Last hour of that day
    };
    const isOverlappingRequestShift = (s) => (
        s.partialShift?.id === shiftRequestRange.id &&
        ongoingRequestStatuses.includes(s.status) &&
        isWithinDates(
            new Date(s.partialFromDate),
            fullShift,
        ));
    const isProposalOverlappingRequestShift = (s) => (
        s.partialShift?.id === shiftRequestRange.id &&
        ongoingProposalStatuses.includes(s.status) &&
        isWithinDates(
            new Date(s.partialFromDate),
            fullShift,
        ));
    const activePartialToCover = !employeeShiftOffers?.length
        ? []
        : employeeShiftOffers
            .filter(isOverlappingRequestShift)
            .map((s) => ({
                partialFromDate: new Date(s.partialFromDate),
                partialToDate: new Date(s.partialToDate),
                fromLabel: formatSliderDate(s.partialFromDate),
                toLabel: formatSliderDate(s.partialToDate),
            }));
    const activeSwapOffers = !employeeMyShiftSwapOffers?.length
        ? []
        : employeeMyShiftSwapOffers
            .filter(isOverlappingRequestShift)
            .map((s) => ({
                partialFromDate: new Date(s.partialFromDate),
                partialToDate: new Date(s.partialToDate),
                fromLabel: formatSliderDate(s.partialFromDate),
                toLabel: formatSliderDate(s.partialToDate),
            }));
    const activeProposals = !myProposals?.length
        ? []
        : myProposals
            .reduce((prev, curr) => {
                const proposal = curr.proposals.find(isProposalOverlappingRequestShift);
                if (proposal){
                    return [...prev, proposal];    
                }
                return prev;
            }, [])
            .map((s) => ({
                partialFromDate: new Date(s.partialFromDate),
                partialToDate: new Date(s.partialToDate),
                fromLabel: formatSliderDate(s.partialFromDate),
                toLabel: formatSliderDate(s.partialToDate),
            }));

    const otherOffers = !miscellaneousOffers?.length
        ? []
        : miscellaneousOffers
            .map((s) => ({
                partialFromDate: new Date(s.alreadyOfferedInPartialStart),
                partialToDate: new Date(s.alreadyOfferedInPartialEnd),
                fromLabel: formatSliderDate(s.alreadyOfferedInPartialStart),
                toLabel: formatSliderDate(s.alreadyOfferedInPartialEnd),
            }))

    return [...activePartialToCover, ...activeSwapOffers, ...activeProposals, ...otherOffers];
}